/**
 * Inactive Clients (BE-14d-2)
 *
 * Customers with no invoice, work order or top-up for the chosen number of days (or never), with what they still
 * hold in their wallet and owe. A row opens the customer's statement.
 * Route: /reports/inactive-clients
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ReportPage, { formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { serverMessage } from '../../utils/apiConvert';

const DAYS = [30, 60, 90, 180];
const ACTIVITY = { INVOICE: 'Invoice', WORK_ORDER: 'Work order', TOP_UP: 'Top-up' };
const money = (v) => (Number(v) ? formatCurrency(v) : '-');

const InactiveClients = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(90);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getInactiveClients({ days }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load inactive clients'));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    { key: 'customer', label: 'Customer' },
    { key: 'lastActivity', label: 'Last activity', render: (r) => (r.lastActivity ? formatDate(r.lastActivity) : 'Never') },
    { key: 'lastActivityType', label: 'Activity', render: (r) => ACTIVITY[r.lastActivityType] || '-' },
    { key: 'daysInactive', label: 'Days inactive', align: 'right', render: (r) => r.daysInactive ?? '-' },
    { key: 'phone', label: 'Phone', render: (r) => r.phone || '-' },
    { key: 'email', label: 'Email', render: (r) => r.email || '-' },
    { key: 'walletBalance', label: 'Wallet', align: 'right', render: (r) => money(r.walletBalance), total: (x) => formatCurrency(x.walletTotal) },
    { key: 'owed', label: 'Owed', align: 'right', render: (r) => money(r.owed), total: (x) => formatCurrency(x.owedTotal) },
  ];

  const cards = report ? [
    { label: 'Inactive customers', value: report.count, tone: report.count > 0 ? 'warn' : undefined },
    { label: 'Wallet held', value: formatCurrency(report.walletTotal) },
    { label: 'Owed', value: formatCurrency(report.owedTotal), tone: Number(report.owedTotal) > 0 ? 'bad' : undefined },
  ] : [];

  return (
    <ReportPage
      title="Inactive Clients"
      subtitle={`No invoice, work order or top-up for ${days} days or more, as of ${formatDate(report?.asOf)}`}
      exportType="inactive-clients" exportParams={{ days }} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={columns} rows={report?.rows || []} rowKey={(r) => r.customerId}
      totals={report} totalsLabel={`TOTAL (${report?.count || 0} customers)`}
      onRowClick={(r) => navigate(`/reports/client-ledger/${r.customerId}`)}
      emptyMessage="Every customer has been active in this period."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>Inactive for at least</Typography>
          </Grid>
          <Grid item>
            <ToggleButtonGroup exclusive size="small" value={days} onChange={(e, v) => v && setDays(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.8125rem', px: 2 } }}>
              {DAYS.map((d) => <ToggleButton key={d} value={d}>{d} days</ToggleButton>)}
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default InactiveClients;
